import { useId, type ReactNode } from 'react';
import type { Slot } from '../api/types';
import { useZeitwahl } from '../provider';
import type { ISODate } from '../time/calendar';
import { formatDayLong, formatTime, formatTimeRange } from '../time/format';

export interface SlotPickerProps {
  /** The visitor-zone day whose slots are listed; `null` shows the "pick a date" hint. */
  date: ISODate | null;
  slots: Slot[];
  timeZone: string;
  onSelect: (slot: Slot) => void;
  selected?: Slot | null;
  /** The slot currently being reserved (shows the "Reserving…" state). */
  pendingSlot?: Slot | null;
  disabled?: boolean;
  loading?: boolean;
  locale?: string;
}

/** Slot buttons with full accessible names: "Monday, March 30, 9:00 AM – 9:30 AM (Europe/Zurich)". */
export function SlotPicker(props: SlotPickerProps): ReactNode {
  const { date, slots, timeZone, onSelect, selected, pendingSlot, disabled, loading } = props;
  const { t, locale: contextLocale } = useZeitwahl();
  const locale = props.locale ?? contextLocale;
  const headingId = useId();

  if (!date) {
    return (
      <div className="zw-slots">
        <p className="zw-muted">{t('slotsPickDate')}</p>
      </div>
    );
  }

  const dayLabel = formatDayLong(date, locale);
  return (
    <section className="zw-slots" aria-labelledby={headingId}>
      <h3 id={headingId} className="zw-slots-title">
        {dayLabel}
      </h3>
      {loading && slots.length === 0 ? (
        <p className="zw-muted" role="status">
          {t('loading')}
        </p>
      ) : slots.length === 0 ? (
        <p className="zw-muted">{t('slotsNone')}</p>
      ) : (
        <ul className="zw-slot-list">
          {slots.map((slot) => {
            const isSelected = selected?.startUtc === slot.startUtc;
            const isPending = pendingSlot?.startUtc === slot.startUtc;
            return (
              <li key={slot.startUtc}>
                <button
                  type="button"
                  className={`zw-slot${isSelected ? ' zw-slot--selected' : ''}`}
                  aria-label={`${dayLabel}, ${formatTimeRange(slot.startUtc, slot.end, locale, timeZone)} (${timeZone})`}
                  aria-pressed={isSelected}
                  aria-busy={isPending || undefined}
                  disabled={disabled}
                  onClick={() => onSelect(slot)}
                >
                  {isPending ? t('slotsHolding') : formatTime(slot.startUtc, locale, timeZone)}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
